// Generated frozen-noir art index (Higgsfield) for FIMBULWAKE. Maps game data
// → asset URLs and provides a tiny preloaded-Image cache for canvas drawing
// on the City Map.

/** Every mob portrait in the pack, keyed by filename slug. Covers the 6 base
 *  archetypes AND all 48 clan-styled units (FAMILY_UNITS type slugs) — drop
 *  a webp in mobs/ and it's live. */
const MOB_FILES = import.meta.glob('../assets/fimbulwake/mobs/*.webp', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

import docks from '../assets/fimbulwake/districts/docks.webp';
import deadMansPier from '../assets/fimbulwake/districts/dead-mans-pier.webp';
import ashlands from '../assets/fimbulwake/districts/ashlands.webp';
import gallowsRow from '../assets/fimbulwake/districts/gallows-row.webp';
import littleItaly from '../assets/fimbulwake/districts/little-italy.webp';
import heights from '../assets/fimbulwake/districts/heights.webp';
import foundry from '../assets/fimbulwake/districts/foundry.webp';
import bloodAlley from '../assets/fimbulwake/districts/blood-alley.webp';
import oldMarket from '../assets/fimbulwake/districts/old-market.webp';
import freightyards from '../assets/fimbulwake/districts/freightyards.webp';
import ginMile from '../assets/fimbulwake/districts/gin-mile.webp';
import motorRow from '../assets/fimbulwake/districts/motor-row.webp';

/** Unit type slug (or balance archetype) → enemy portrait. */
export const MOB_ART: Record<string, string> = Object.fromEntries(
  Object.entries(MOB_FILES).map(([path, url]) => [
    path.split('/').pop()!.replace('.webp', ''),
    url,
  ]),
);

/** The 72 elite beasts — six per hold (ELITE_ROSTER server-side, bestiary and
 *  art prompts in production/art/elite-bestiary.md). Each has its own
 *  `mobs/<slug>.webp`; should one ever be missing, the slug falls back to the
 *  THEMATICALLY NEAREST world-boss art (owner 01.09: reuse the mythics'
 *  sprites, not one identical warlord) — serpents and sea things wear the
 *  Coil, the dead and the spectral wear the Draugr King, trolls and jotunn-kin
 *  wear the Jotunn, everything else wears Fenrir-Spawn. The real file always
 *  wins: the glob above fills MOB_ART first and the alias only patches holes. */
export const ELITE_TYPES = [
  // The Icewharf
  'wharf-lindworm', 'frost-tusk-walrus', 'hook-draugr', 'fog-widow', 'icebreak-troll', 'claw-crab',
  // Gallows Fell
  'gallows-raven', 'noose-hound', 'gallows-goat', 'hanged-draugr', 'keening-shade', 'gibbet-troll',
  // The Thingstead
  'thingstead-boar', 'stall-elk', 'oathbreaker-draugr', 'weighing-wraith', 'stall-troll', 'grain-weevil',
  // Odal Row
  'odal-wolfhound', 'boundary-stag', 'oathkin-draugr', 'kin-shade', 'boundary-troll', 'watch-hawk',
  // The Forgeholm
  'forge-troll', 'bellows-ram', 'furnace-lynx', 'smith-draugr', 'ember-wisp', 'forge-wasp',
  // The Sledgeways
  'sledge-ox', 'runner-wolf', 'cargo-bull', 'cargo-wraith', 'yard-jotunn', 'frost-shrike',
  // The Ashfjord
  'ash-wraith', 'cinder-fox', 'ash-boar', 'pyre-draugr', 'ember-jotunn', 'ash-moth',
  // Mead Row
  'mead-bear', 'brawler-steed', 'bench-draugr', 'skald-shade', 'cellar-troll', 'mead-wasp',
  // Bloodvein Run
  'blood-wyrm', 'vendetta-hound', 'feud-draugr', 'vein-wraith', 'debt-troll', 'blood-fly',
  // The Longship Yards
  'keel-kraken', 'prow-ram', 'yard-mastiff', 'shipwright-draugr', 'rigging-wraith', 'keelhaul-jotunn',
  // Dead Man's Fjord
  'drowned-shade', 'wreck-otter', 'fjord-eel', 'wreck-draugr', 'fjord-troll', 'pier-cormorant',
  // The High Seat
  'thing-eagle', 'summit-goat', 'storm-ram', 'jarl-draugr', 'wind-shade', 'seatguard-jotunn',
] as const;

/** Slug → nearest mythic sprite, by what the name says the creature is. A
 *  table of 72 hand-written aliases would rot the moment the roster grows. */
const ELITE_STANDIN_RULES: [RegExp, string][] = [
  [/draugr|shade|wraith|widow|wisp|drowned/, 'draugr-king'],
  [/troll|jotunn/, 'frost-jotunn'],
  [/wyrm|lindworm|kraken|eel|crab|walrus|otter/, 'jormungandr-coil'],
];
export const ELITE_ART_STANDIN: Record<string, string> = Object.fromEntries(
  ELITE_TYPES.map((slug) => [
    slug,
    ELITE_STANDIN_RULES.find(([re]) => re.test(slug))?.[1] ?? 'fenrir-spawn',
  ]),
);
for (const slug of ELITE_TYPES) {
  if (!MOB_ART[slug]) MOB_ART[slug] = MOB_ART[ELITE_ART_STANDIN[slug]] ?? MOB_ART['kingpin'];
}

/** Ward display name → aerial key-art. */
export const DISTRICT_ART: Record<string, string> = {
  'The Icewharf': docks,
  "Dead Man's Fjord": deadMansPier,
  'The Ashfjord': ashlands,
  'Gallows Fell': gallowsRow,
  'Odal Row': littleItaly,
  'The High Seat': heights,
  'The Forgeholm': foundry,
  'Bloodvein Run': bloodAlley,
  'The Thingstead': oldMarket,
  'The Sledgeways': freightyards,
  'Mead Row': ginMile,
  'The Longship Yards': motorRow,
};

export const districtArt = (name?: string | null): string | undefined =>
  name ? DISTRICT_ART[name] : undefined;

// ── Equipment icons by slot/type ────────────────────────────────────────────
import weaponIcon from '../assets/fimbulwake/items/weapon.webp';
import helmetIcon from '../assets/fimbulwake/items/helmet.webp';
import armorIcon from '../assets/fimbulwake/items/armor.webp';
import bootsIcon from '../assets/fimbulwake/items/boots.webp';
import ringIcon from '../assets/fimbulwake/items/ring.webp';
import amuletIcon from '../assets/fimbulwake/items/amulet.webp';

import weaponLow from '../assets/fimbulwake/items/weapon-low.webp';
import weaponHigh from '../assets/fimbulwake/items/weapon-high.webp';
import armorLow from '../assets/fimbulwake/items/armor-low.webp';
import armorHigh from '../assets/fimbulwake/items/armor-high.webp';
import helmetLow from '../assets/fimbulwake/items/helmet-low.webp';
import helmetHigh from '../assets/fimbulwake/items/helmet-high.webp';

/** Artifact type → item icon (base / Rare tier). */
export const ITEM_ART: Record<string, string> = {
  WEAPON: weaponIcon,
  HELMET: helmetIcon,
  ARMOR: armorIcon,
  BOOTS: bootsIcon,
  RING: ringIcon,
  AMULET: amuletIcon,
};

/** Per-rarity-tier icons (low = Common/Uncommon, mid = Rare, high = Epic/Legendary). */
const ITEM_TIERS: Record<string, { low?: string; mid: string; high?: string }> = {
  WEAPON: { low: weaponLow, mid: weaponIcon, high: weaponHigh },
  ARMOR: { low: armorLow, mid: armorIcon, high: armorHigh },
  HELMET: { low: helmetLow, mid: helmetIcon, high: helmetHigh },
  BOOTS: { mid: bootsIcon },
  RING: { mid: ringIcon },
  AMULET: { mid: amuletIcon },
};
function tierOf(rarity?: string | null): 'low' | 'mid' | 'high' {
  if (rarity === 'COMMON' || rarity === 'UNCOMMON') return 'low';
  if (rarity === 'EPIC' || rarity === 'LEGENDARY') return 'high';
  // Explicit: falls through to 'mid' otherwise, which would render Mythic gear
  // with the Rare-tier generic icon until bespoke Mythic art exists.
  if (rarity === 'MYTHIC') return 'high';
  return 'mid';
}

/**
 * Per-item art, keyed by the item's name slug (matches `items/item-defs.ts`).
 * Drop a `items/named/<slug>.webp` and it's live — no code change. Slugs must
 * match slugify(name) below.
 */
const NAMED_ITEM_ART = import.meta.glob<string>('../assets/fimbulwake/items/named/*.webp', {
  eager: true, import: 'default', query: '?url',
});
const ITEM_ART_BY_SLUG: Record<string, string> = {};
for (const [path, url] of Object.entries(NAMED_ITEM_ART)) {
  const slug = path.split('/').pop()!.replace(/\.webp$/, '');
  ITEM_ART_BY_SLUG[slug] = url;
}

/** name → art slug. MUST match the backend def slug (drop apostrophes, non-alnum → '-'). */
export function itemSlug(name?: string | null): string {
  return (name ?? '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Item icon. Prefers the item's OWN art (resolved from its name), falling back
 * to the per-slot / per-rarity-tier generic icon for items without bespoke art
 * (older saved gear, or defs whose asset hasn't been generated yet).
 */
export const itemArt = (type?: string | null, rarity?: string | null, name?: string | null): string | undefined => {
  if (name) {
    const own = ITEM_ART_BY_SLUG[itemSlug(name)];
    if (own) return own;
  }
  if (!type) return undefined;
  const t = ITEM_TIERS[type];
  if (!t) return ITEM_ART[type];
  const tier = tierOf(rarity);
  return t[tier] ?? t.mid;
};

/**
 * 104px thumb of the same icon, for the SMALL renders (Market tiles 52px,
 * sea-chest 44px, paperdoll, loot rows, toasts) — 256px originals were ~3x the
 * bytes those surfaces can display. 104 = 52 x 2, so retina stays sharp; the
 * container is fixed-size at every viewport, so a single 2x asset covers all
 * DPRs and no srcset is needed. Generated by scripts/make-item-thumbs.py; a
 * missing thumb falls back to the full icon, so the pipeline can't break art.
 * Big renders (ItemModal, ChestReveal at 168px) keep using itemArt().
 */
const NAMED_ITEM_THUMBS = import.meta.glob<string>('../assets/fimbulwake/items/named/thumbs/*.webp', {
  eager: true, import: 'default', query: '?url',
});
const ITEM_THUMB_BY_SLUG: Record<string, string> = {};
for (const [path, url] of Object.entries(NAMED_ITEM_THUMBS)) {
  ITEM_THUMB_BY_SLUG[path.split('/').pop()!.replace(/\.webp$/, '')] = url;
}
export const itemThumb = (type?: string | null, rarity?: string | null, name?: string | null): string | undefined => {
  if (name) {
    const thumb = ITEM_THUMB_BY_SLUG[itemSlug(name)];
    if (thumb) return thumb;
  }
  return itemArt(type, rarity, name);
};

// ── Hero class portraits ────────────────────────────────────────────────────
import warriorArt from '../assets/fimbulwake/crew-classes/warrior.webp';
import paladinArt from '../assets/fimbulwake/crew-classes/paladin.webp';
import archerArt from '../assets/fimbulwake/crew-classes/archer.webp';
import mageArt from '../assets/fimbulwake/crew-classes/mage.webp';

/** HeroClass → crew portrait (Norse crime-saga archetypes). */
export const CLASS_ART: Record<string, string> = {
  WARRIOR: warriorArt,
  PALADIN: paladinArt,
  ARCHER: archerArt,
  MAGE: mageArt,
};
export const classArt = (cls?: string | null): string | undefined =>
  cls ? CLASS_ART[cls] : undefined;

// ── The living hero — a per-class idle loop with a real alpha channel, keyed
//    from a generated green-screen take of the very portrait above (Kling i2v,
//    ping-pong assembled so the loop has no seam). Two encodings of the same
//    clip because no single alpha codec crosses the browser line: VP9 WebM for
//    Chromium/Firefox, HEVC (hvc1) MOV for Safari.
import warriorIdleWebm from '../assets/fimbulwake/crew-classes/warrior-idle.webm';
import warriorIdleMov from '../assets/fimbulwake/crew-classes/warrior-idle.mov';
import paladinIdleWebm from '../assets/fimbulwake/crew-classes/paladin-idle.webm';
import paladinIdleMov from '../assets/fimbulwake/crew-classes/paladin-idle.mov';
import archerIdleWebm from '../assets/fimbulwake/crew-classes/archer-idle.webm';
import archerIdleMov from '../assets/fimbulwake/crew-classes/archer-idle.mov';
import mageIdleWebm from '../assets/fimbulwake/crew-classes/mage-idle.webm';
import mageIdleMov from '../assets/fimbulwake/crew-classes/mage-idle.mov';

const CLASS_IDLE: Record<string, { webm: string; mov: string }> = {
  WARRIOR: { webm: warriorIdleWebm, mov: warriorIdleMov },
  PALADIN: { webm: paladinIdleWebm, mov: paladinIdleMov },
  ARCHER: { webm: archerIdleWebm, mov: archerIdleMov },
  MAGE: { webm: mageIdleWebm, mov: mageIdleMov },
};

// Safari cannot be given the WebM: it PLAYS VP9 but drops the alpha channel,
// which puts the figure on a black slab. And <source> order cannot decide it —
// HEVC-capable Chromium answers "maybe" to hvc1 yet renders its alpha the same
// broken way. UA-gate: only Safari gets the MOV, everyone else the WebM.
const isSafari = typeof navigator !== 'undefined'
  && /safari/i.test(navigator.userAgent)
  && !/chrome|chromium|crios|edg|android/i.test(navigator.userAgent);

/** HeroClass → idle-loop video URL for this browser (undefined for unknown class). */
export const classIdle = (cls?: string | null): string | undefined => {
  const set = cls ? CLASS_IDLE[cls] : undefined;
  return set ? (isSafari ? set.mov : set.webm) : undefined;
};

// ── Combat fighter poses per class (idle/attack/heavy/ability/guard/hurt) ────
const FIGHTER_MODULES = import.meta.glob<string>('../assets/fimbulwake/fighters/*.webp', {
  eager: true, import: 'default',
});
export type FighterPose = 'idle' | 'attack' | 'heavy' | 'ability' | 'guard' | 'hurt';
const FIGHTER_ART: Record<string, Partial<Record<FighterPose, string>>> = {};
for (const [path, url] of Object.entries(FIGHTER_MODULES)) {
  const m = path.match(/\/([a-z]+)-([a-z]+)\.webp$/);
  if (!m) continue;
  const cls = m[1].toUpperCase();
  (FIGHTER_ART[cls] ??= {})[m[2] as FighterPose] = url;
}
/** Pose art for a hero class (falls back through attack → idle → class portrait). */
export const fighterPose = (cls?: string | null, pose: FighterPose = 'idle'): string | undefined => {
  if (!cls) return undefined;
  const set = FIGHTER_ART[cls];
  return set?.[pose] || set?.attack || set?.idle || classArt(cls);
};

// ── Scene backgrounds ───────────────────────────────────────────────────────
import bgMarket from '../assets/fimbulwake/backgrounds/market.webp';

import bgLogin from '../assets/fimbulwake/backgrounds/login.webp';
import bgOffice from '../assets/fimbulwake/backgrounds/office.webp';
import bgJobs from '../assets/fimbulwake/backgrounds/jobs.webp';
import bgRankings from '../assets/fimbulwake/backgrounds/rankings.webp';

import bgStudy from '../assets/fimbulwake/backgrounds/study.webp';
import outVictory from '../assets/fimbulwake/outcomes/victory.webp';
import outDefeat from '../assets/fimbulwake/outcomes/defeat.webp';

/** Fight outcome banners. */
export const OUTCOME = { victory: outVictory, defeat: outDefeat };
export const outcomeArt = (won: boolean): string => (won ? outVictory : outDefeat);

// ── Rank medals (top-3) + tier badge ────────────────────────────────────────
import rankGold from '../assets/fimbulwake/ranks/gold.webp';
import rankSilver from '../assets/fimbulwake/ranks/silver.webp';
import rankBronze from '../assets/fimbulwake/ranks/bronze.webp';
import rankTier from '../assets/fimbulwake/ranks/tier.webp';

/** Medal for a leaderboard position (1-3), or undefined below the podium. */
export const rankMedal = (rank: number): string | undefined =>
  rank === 1 ? rankGold : rank === 2 ? rankSilver : rank === 3 ? rankBronze : undefined;

/** Generic saga-tier rank pin. */
export const tierBadge = (): string => rankTier;

// ── Combat VFX sprites (on black — composite with mix-blend-mode: screen) ────
import fxMuzzle from '../assets/fimbulwake/fx/muzzle.webp';
import fxSpark from '../assets/fimbulwake/fx/spark.webp';
import fxBlood from '../assets/fimbulwake/fx/blood.webp';
import fxAcid from '../assets/fimbulwake/fx/acid.webp';

const FX_ART: Record<string, string> = { muzzle: fxMuzzle, spark: fxSpark, blood: fxBlood, acid: fxAcid };
export const fxArt = (kind?: string | null): string | undefined => (kind ? FX_ART[kind] : undefined);

import bgStash from '../assets/fimbulwake/backgrounds/stash.webp';
import bgShop from '../assets/fimbulwake/backgrounds/shop.webp';
import bgPit from '../assets/fimbulwake/backgrounds/pit.webp';
import bgFamily from '../assets/fimbulwake/backgrounds/family.webp';

// ── UI plates ───────────────────────────────────────────────────────────────
// Surfaces that cannot be drawn in CSS: perspective, top-down carpentry, or a lit
// height that a gradient can only imitate. Everything else in this build's chrome
// IS CSS/SVG — these five earn their bytes.
import uiRuneRing from '../assets/fimbulwake/ui/rune-ring.webp';
import uiMarketBeam from '../assets/fimbulwake/ui/market-beam.webp';
import uiChestTray from '../assets/fimbulwake/ui/chest-tray.webp';
import uiWeaponRail from '../assets/fimbulwake/ui/weapon-rail.webp';
import uiDaybook from '../assets/fimbulwake/ui/daybook.webp';

export const UI = {
  /** Carved rune disc on PURE BLACK — composite with `screen`/`lighten`, never
   *  `normal`, or you paste a black square under the figure. */
  runeRing: uiRuneRing,
  marketBeam: uiMarketBeam,
  chestTray: uiChestTray,
  weaponRail: uiWeaponRail,
  daybook: uiDaybook,
};

export const BG = {
  market: bgMarket, login: bgLogin, office: bgOffice, study: bgStudy,
  jobs: bgJobs, rankings: bgRankings,
  stash: bgStash, shop: bgShop, pit: bgPit, family: bgFamily,
};

// ── Raid (dungeon) scene art by id ─────────────────────────────────────────
import heistSpeakeasy from '../assets/fimbulwake/heists/speakeasy_raid.webp';
import heistDock from '../assets/fimbulwake/heists/dock_heist.webp';
import heistBank from '../assets/fimbulwake/heists/bank_job.webp';
import heistKingpin from '../assets/fimbulwake/heists/kingpin_sitdown.webp';

export const HEIST_ART: Record<string, string> = {
  speakeasy_raid: heistSpeakeasy,
  dock_heist: heistDock,
  bank_job: heistBank,
  kingpin_sitdown: heistKingpin,
};
export const heistArt = (id?: string | null): string | undefined =>
  id ? HEIST_ART[id] : undefined;

// ── Clan crests (by ward name AND clan name) ─────────────────────────────────
import crestDoyle from '../assets/fimbulwake/crests/doyle.webp';
import crestLedger from '../assets/fimbulwake/crests/ledger.webp';
import crestSwitchmen from '../assets/fimbulwake/crests/switchmen.webp';
import crestVelvet from '../assets/fimbulwake/crests/velvet-chorus.webp';
import crestCarbide from '../assets/fimbulwake/crests/carbide.webp';
import crestCinder from '../assets/fimbulwake/crests/cinder.webp';
import crestGallows from '../assets/fimbulwake/crests/gallows.webp';
import crestMoretti from '../assets/fimbulwake/crests/moretti.webp';
import crestCommission from '../assets/fimbulwake/crests/commission.webp';
import crestKowalski from '../assets/fimbulwake/crests/kowalski.webp';
import crestRedLantern from '../assets/fimbulwake/crests/red-lantern.webp';
import crestWidow from '../assets/fimbulwake/crests/widow.webp';

/** Crest keyed by BOTH the ward name and the clan name it belongs to. */
export const CREST_ART: Record<string, string> = {
  'The Icewharf': crestDoyle, 'The Saltbeards': crestDoyle,
  'The Ashfjord': crestCinder, 'The Cinderborn': crestCinder,
  'Gallows Fell': crestGallows, 'The Hanged Ones': crestGallows,
  'Odal Row': crestMoretti, 'The Osvald Kin': crestMoretti,
  'The High Seat': crestCommission, 'The Althing': crestCommission,
  'The Forgeholm': crestKowalski, 'The Emberhands': crestKowalski,
  'Bloodvein Run': crestRedLantern, 'The Red Wyrms': crestRedLantern,
  'The Thingstead': crestLedger, 'The Silvertongues': crestLedger,
  'The Sledgeways': crestSwitchmen, 'The Sledgemen': crestSwitchmen,
  'Mead Row': crestVelvet, "The Skald's Chorus": crestVelvet,
  'The Longship Yards': crestCarbide, 'The Keelbreakers': crestCarbide,
  "Dead Man's Fjord": crestWidow, "The Widow's Wake": crestWidow,
};
export const crestArt = (key?: string | null): string | undefined =>
  key ? CREST_ART[key] : undefined;

// ── Combat action scenes (frozen-noir cinematic backdrops for the fight action
//    bar buttons): the axe-strike, the heavy swing, hunkering behind a shield,
//    the class's signature move, a pull from the horn of mead, the sled
//    getaway. ───────────────────────────────────────────────────────────────
import scStrike from '../assets/fimbulwake/combat/scenes/strike.webp';
import scHeavy from '../assets/fimbulwake/combat/scenes/heavy.webp';
import scGuard from '../assets/fimbulwake/combat/scenes/guard.webp';
import scAbility from '../assets/fimbulwake/combat/scenes/ability.webp';
import scPatch from '../assets/fimbulwake/combat/scenes/patch.webp';
import scFlee from '../assets/fimbulwake/combat/scenes/flee.webp';

export const COMBAT_SCENE: Record<string, string> = {
  strike: scStrike, heavy: scHeavy, guard: scGuard,
  ability: scAbility, patch: scPatch, flee: scFlee,
};

// ── Shop consumable icons by effect kind ────────────────────────────────────
import shopNerve from '../assets/fimbulwake/shop/nerve.webp';
import shopHealth from '../assets/fimbulwake/shop/health.webp';
import shopXp from '../assets/fimbulwake/shop/xp.webp';
import shopCash from '../assets/fimbulwake/shop/cash.webp';
import shopRevive from '../assets/fimbulwake/shop/revive.webp';
import shopChest from '../assets/fimbulwake/shop/chest.webp';

/** Shop effect kind → icon. */
export const SHOP_ART: Record<string, string> = {
  nerve_refill: shopNerve,
  hp_refill: shopHealth,
  hp_partial: shopHealth,
  hp_fixed: shopHealth,
  xp_booster: shopXp,
  cash_rush: shopCash,
  revive: shopRevive,
  chest_pull: shopChest,
  vault_artifact: weaponIcon,
  inventory_slot: shopChest,
};
export const shopArt = (kind?: string | null): string | undefined =>
  kind ? SHOP_ART[kind] : undefined;

// ── Preloaded Image cache (for <canvas> drawImage) ──────────────────────────
const cache = new Map<string, HTMLImageElement>();
function img(url: string): HTMLImageElement {
  let im = cache.get(url);
  if (!im) {
    im = new Image();
    im.src = url;
    cache.set(url, im);
  }
  return im;
}

/** Mob portrait URL — tries the family-styled unit slug first, then falls
 *  back to the base balance archetype (so a missing skin never blanks art). */
export const mobArt = (type?: string | null, archetype?: string | null): string | undefined =>
  (type ? MOB_ART[type] : undefined) ?? (archetype ? MOB_ART[archetype] : undefined);

/** A ready-to-draw mob portrait (slug → archetype fallback), or null. */
export function mobImg(type?: string, archetype?: string): HTMLImageElement | null {
  const url = mobArt(type, archetype);
  if (!url) return null;
  const im = img(url);
  return im.complete && im.naturalWidth > 0 ? im : (void img(url), null);
}

/** A ready-to-draw hero class portrait (for the map's player marker), or null. */
export function classImg(cls?: string | null): HTMLImageElement | null {
  const url = cls ? CLASS_ART[cls] : undefined;
  if (!url) return null;
  const im = img(url);
  return im.complete && im.naturalWidth > 0 ? im : (void img(url), null);
}

/** A ready-to-draw family crest (by district or family name), or null. */
export function crestImg(key?: string): HTMLImageElement | null {
  if (!key) return null;
  const url = CREST_ART[key];
  if (!url) return null;
  const im = img(url);
  return im.complete && im.naturalWidth > 0 ? im : (void img(url), null);
}

// ── Fight screen art ────────────────────────────────────────────────────────
// The dedicated bout screen: full-body class poses, the six pre-rendered action
// cards, the docks backdrop and the two HP-bar frames. Keyed by engine HeroClass
// (dir name lowercased): warrior=Berserker, paladin=Huskarl, archer=Hunter,
// mage=Völva. Card art has its number AND title baked in, so the order is fixed
// (1 STRIKE · 2 HEAVY · 3 GUARD · 4 FLEE · 5 class ability · 6 PATCH UP) and no
// text is drawn over it.
const FIGHT_MODULES = import.meta.glob<string>('../assets/fimbulwake/fight/**/*.webp', {
  eager: true, import: 'default',
});
export type FightPose =
  | 'default' | 'strike' | 'heavy' | 'guard' | 'flee'
  | 'ability' | 'patch-up' | 'damage' | 'damage-2' | 'win' | 'defeat';

const FIGHT_ART: Record<string, Record<string, string>> = {};
const FIGHT_SHARED: Record<string, string> = {};
for (const [path, url] of Object.entries(FIGHT_MODULES)) {
  // `[a-z-]+` not `[a-z]+`: mythic archetypes are hyphenated (fenrir-spawn,
  // draugr-king, …) and their pose dirs must resolve like any fighter's.
  const perClass = path.match(/\/fight\/([a-z-]+)\/([a-z0-9-]+)\.webp$/);
  if (perClass) {
    (FIGHT_ART[perClass[1].toUpperCase()] ??= {})[perClass[2]] = url;
    continue;
  }
  const shared = path.match(/\/fight\/([a-z-]+)\.webp$/);
  if (shared) FIGHT_SHARED[shared[1]] = url;
}

/** The four mythic world-boss archetypes — they carry full pose sets like any
 *  fighter, and the fight screen renders them a head taller than everyone. */
export const MYTHIC_ARCHETYPES = new Set(['fenrir-spawn', 'jormungandr-coil', 'draugr-king', 'frost-jotunn']);

/** Nearest sibling frame for a pose a set does not ship. The classes and the
 *  mythics carry the full grid, so this only fires for the 72 elite beasts —
 *  they shoot the four poses that read (idle, strike, damage, win, defeat) and
 *  borrow across for the rest, which beats dropping to the idle: a heavy blow
 *  should still swing, and a second hit should still recoil. */
const POSE_KIN: Partial<Record<FightPose, FightPose>> = {
  heavy: 'strike',
  ability: 'strike',
  'damage-2': 'damage',
  guard: 'default',
  flee: 'default',
  'patch-up': 'default',
};

/** Pose art for a class on the fight screen. Falls back through the nearest
 *  kin pose, then the idle `default`. */
export const fightPose = (cls?: string | null, pose: FightPose = 'default'): string | undefined => {
  if (!cls) return undefined;
  const set = FIGHT_ART[cls.toUpperCase()];
  if (!set) return undefined;
  const kin = POSE_KIN[pose];
  return set[pose] ?? (kin ? set[kin] : undefined) ?? set.default;
};

/** One of the six action cards (1-6) for a class. */
export const fightCard = (cls?: string | null, n = 1): string | undefined =>
  cls ? FIGHT_ART[cls.toUpperCase()]?.[`card-${n}`] : undefined;

export const FIGHT_BG = FIGHT_SHARED.docks;

/* HP_BAR is GONE. It was a 1414x140 raster frame — "ONE ornate art-deco frame" by
   its own former comment — plus measured groove insets so a CSS fill could sit
   inside the art. The fight HUD draws shield boards in a CSS iron channel now
   (FightScreen.vue `.fs-track`), which is the same gauge the hero card, the raid
   party and the ward chests use. Seven hp-*.webp files went with it. */
